import { useMemo, useState, type ReactNode } from 'react'
import { Plus, Trash2, HardDrive, Server as ServerIcon } from 'lucide-react'
import {
  type StructureData,
  type Drive,
  type DriveType,
  DRIVE_TYPE_LABELS,
} from '../types.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  Input,
  Select,
  type ColumnDef,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { ResponsiblePicker } from './matrix/ResponsiblePicker.tsx'

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

const RESERVED_LETTERS = new Set(['A', 'B', 'C', 'D'])

function suggestNextLetter(existingLetters: Set<string>): string {
  for (let code = 'E'.charCodeAt(0); code <= 'Z'.charCodeAt(0); code++) {
    const l = String.fromCharCode(code)
    if (!existingLetters.has(l)) return l
  }
  return ''
}

export function DrivesTab({ data, update }: Props) {
  const [error, setError] = useState<string | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<
    | {
        title: string
        message: ReactNode
        confirmLabel?: string
        onConfirm: () => void
      }
    | null
  >(null)

  const allShares = data.servers.flatMap((s) =>
    s.shares.map((sh) => ({ server: s, share: sh })),
  )

  const shareOptions = allShares.map(({ server, share }) => ({
    value: share.id,
    label: `${server.name} · ${share.name}${share.isHidden ? '$' : ''}`,
  }))

  const driveTypeOptions = (Object.keys(DRIVE_TYPE_LABELS) as DriveType[]).map((t) => ({
    value: t,
    label: DRIVE_TYPE_LABELS[t],
  }))

  function addDrive() {
    setError(null)
    if (allShares.length === 0) {
      setError('Bitte zuerst im Tab "Server & Shares" einen Share anlegen.')
      return
    }
    const first = allShares[0]
    const takenLetters = new Set(data.drives.map((d) => d.letter.toUpperCase()))
    const nextLetter = suggestNextLetter(takenLetters)
    const drive: Drive = {
      id: crypto.randomUUID(),
      shareId: first.share.id,
      letter: nextLetter,
      label: '',
      subPath: '',
      type: 'team',
    }
    update({ drives: [...data.drives, drive] })
  }

  function updateDrive(id: string, patch: Partial<Drive>) {
    setError(null)
    update({
      drives: data.drives.map((d) => {
        if (d.id !== id) return d
        const merged: Drive = { ...d, ...patch }
        if (patch.letter !== undefined) {
          merged.letter = patch.letter.toUpperCase().slice(0, 1)
        }
        return merged
      }),
    })
  }

  function deleteDrive(id: string) {
    update({
      drives: data.drives.filter((d) => d.id !== id),
      folders: data.folders.map((f) => (f.driveId === id ? { ...f, driveId: undefined } : f)),
    })
  }

  function requestDeleteDrive(id: string) {
    const drive = data.drives.find((d) => d.id === id)
    if (!drive) return
    const share = allShares.find((x) => x.share.id === drive.shareId)?.share
    const label = drive.letter
      ? `${drive.letter}:${drive.label ? ` (${drive.label})` : ''}`
      : drive.label || '(ohne Label)'
    setConfirmDialog({
      title: 'Laufwerk löschen',
      confirmLabel: 'Laufwerk löschen',
      message: (
        <div className="space-y-2">
          <p>
            Soll das Laufwerks-Mapping <b>{label}</b> wirklich gelöscht werden?
          </p>
          {share && (
            <p className="text-caption text-gray-600">
              Share: <code className="font-mono">{share.name}</code>
              {drive.subPath ? (
                <>
                  , Sub-Pfad: <code className="font-mono">{drive.subPath}</code>
                </>
              ) : null}
            </p>
          )}
          <p className="text-caption text-gray-600">
            Der Share und die Ordner bleiben erhalten — nur der Laufwerksbuchstabe wird entfernt.
          </p>
        </div>
      ),
      onConfirm: () => {
        deleteDrive(id)
        setConfirmDialog(null)
      },
    })
  }

  const sortedDrives = [...data.drives].sort((a, b) => {
    if (!a.letter) return 1
    if (!b.letter) return -1
    return a.letter.localeCompare(b.letter)
  })

  const letterCounts = new Map<string, number>()
  for (const d of data.drives) {
    if (!d.letter) continue
    letterCounts.set(d.letter, (letterCounts.get(d.letter) ?? 0) + 1)
  }

  function previewUnc(drive: Drive): string {
    const match = allShares.find((x) => x.share.id === drive.shareId)
    if (!match) return ''
    const base = match.share.uncPath.replace(/\\+$/, '')
    const sub = (drive.subPath ?? '').replace(/^\\+|\\+$/g, '')
    return sub ? `${base}\\${sub}` : base
  }

  const driveColumns = useMemo<ColumnDef<Drive, unknown>[]>(
    () => [
      {
        accessorKey: 'letter',
        header: 'Buchstabe',
        size: 90,
        cell: ({ row }) => {
          const d = row.original
          const isDuplicate = d.letter ? (letterCounts.get(d.letter) ?? 0) > 1 : false
          const isReserved = RESERVED_LETTERS.has(d.letter)
          return (
            <div className="flex items-center gap-1">
              <input
                type="text"
                maxLength={1}
                value={d.letter}
                onChange={(e) => updateDrive(d.id, { letter: e.target.value })}
                placeholder="?"
                className={cn(
                  'w-10 px-2 py-1 border rounded text-center font-mono font-bold',
                  isDuplicate
                    ? 'border-red-300 bg-red-50 text-red-700'
                    : isReserved
                      ? 'border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-gray-300',
                )}
                title={
                  isDuplicate
                    ? 'Buchstabe doppelt vergeben!'
                    : isReserved
                      ? 'Buchstabe ist meist durch lokale Platten belegt.'
                      : undefined
                }
              />
              <span className="text-gray-400 font-mono text-caption">:</span>
            </div>
          )
        },
      },
      {
        accessorKey: 'label',
        header: 'Label',
        cell: ({ row }) => (
          <Input
            type="text"
            value={row.original.label}
            onChange={(e) => updateDrive(row.original.id, { label: e.target.value })}
            placeholder="z.B. ZentraleBereiche"
            className="py-1 text-caption"
          />
        ),
      },
      {
        accessorKey: 'shareId',
        header: 'Share',
        cell: ({ row }) => (
          <div className="min-w-[180px]">
            <Select
              value={row.original.shareId}
              onValueChange={(v) => updateDrive(row.original.id, { shareId: v })}
              options={shareOptions}
              placeholder="(kein Share)"
              triggerClassName="py-1 text-caption font-mono"
            />
            <p
              className="mt-1 text-caption text-gray-400 font-mono truncate"
              title={previewUnc(row.original)}
            >
              {previewUnc(row.original) || '—'}
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'subPath',
        header: 'Sub-Pfad',
        cell: ({ row }) => (
          <Input
            type="text"
            value={row.original.subPath ?? ''}
            onChange={(e) => updateDrive(row.original.id, { subPath: e.target.value })}
            placeholder={row.original.type === 'personal' ? 'Users\\%USERNAME%' : '(= Share-Root)'}
            className="py-1 text-caption font-mono"
          />
        ),
      },
      {
        accessorKey: 'type',
        header: 'Typ',
        size: 150,
        cell: ({ row }) => (
          <Select
            value={row.original.type}
            onValueChange={(v) => updateDrive(row.original.id, { type: v as DriveType })}
            options={driveTypeOptions}
            triggerClassName="py-1 text-caption"
          />
        ),
      },
      {
        id: 'responsibles',
        header: 'Verantwortliche',
        size: 260,
        cell: ({ row }) => (
          <ResponsiblePicker
            value={row.original.responsibleEmployees ?? []}
            onChange={(next) =>
              updateDrive(row.original.id, { responsibleEmployees: next.length > 0 ? next : undefined })
            }
            inherited={null}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ row }) => (
          <IconButton
            icon={<Trash2 className="w-3.5 h-3.5" />}
            aria-label="Laufwerk löschen"
            variant="ghost"
            size="xs"
            onClick={() => requestDeleteDrive(row.original.id)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shareOptions, driveTypeOptions, letterCounts, allShares],
  )

  return (
    <div className="space-y-4">
      <Card className="border-blue-200 bg-blue-50">
        <div className="px-4 py-3 text-body text-blue-800">
          <p className="font-medium mb-1 flex items-center gap-1.5">
            <HardDrive className="w-4 h-4" />
            Laufwerks-Mapping
          </p>
          <p className="text-caption text-blue-700">
            Jedes Laufwerk mappt einen Buchstaben (z.B. <code className="font-mono bg-white px-1">P:</code>) per GPO
            auf einen (Unter-)Pfad innerhalb eines Shares. Ein Share kann mehrere Laufwerke haben.
            Typ <b>Persönlich</b> nutzt <code className="font-mono bg-white px-1">%USERNAME%</code>-Platzhalter und die FU-Filtergruppe;
            <b> Team</b>-Laufwerke bekommen pro Buchstabe eine eigene <code className="font-mono bg-white px-1">{'<Prefix>_LW_<Buchstabe>'}</code>-Gruppe.
          </p>
        </div>
      </Card>

      {error && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="px-3 py-2 text-body text-amber-800">{error}</div>
        </Card>
      )}

      <Card>
        <Card.Header>
          <Card.Title className="text-body flex items-center gap-2">
            <HardDrive className="w-4 h-4" />
            Laufwerke ({data.drives.length})
          </Card.Title>
          <Button
            variant="ghost"
            size="xs"
            leadingIcon={<Plus className="w-3.5 h-3.5" />}
            onClick={addDrive}
          >
            Laufwerk
          </Button>
        </Card.Header>

        {data.drives.length === 0 ? (
          <EmptyState size="sm" icon={<HardDrive />} title="Noch keine Laufwerke definiert" />
        ) : (
          <DataTable<Drive>
            data={sortedDrives}
            getRowId={(d) => d.id}
            enableSorting={false}
            columns={driveColumns}
          />
        )}
      </Card>

      {allShares.length === 0 && (
        <Card>
          <div className="p-4 text-body text-gray-500 flex items-start gap-2">
            <ServerIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              Du hast noch keinen Share angelegt. Wechsle in den Tab <b>Server &amp; Shares</b>,
              lege einen Server + Share an, und komme dann hierher zurück, um die Laufwerksbuchstaben
              zu definieren.
            </div>
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={confirmDialog !== null}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        confirmLabel={confirmDialog?.confirmLabel}
        variant="danger"
        onConfirm={() => confirmDialog?.onConfirm()}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  )
}
